import { ReactNode, useMemo } from 'react';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  useSessionMinutes,
  useSessionSensorUtilization,
} from '../../../hooks/analytics/useAnalytics';
import { MetricCard } from '../overview/MetricCard';
import { SkeletonLoader } from '../shared/SkeletonLoader';
import { chartUtils } from '../utils/chartUtils';
import { formatNumber } from '../utils/dataUtils';
import { MetricHelpTooltip } from '../shared';
import { AnalyticsErrorBoundary } from '../shared/AnalyticsErrorBoundary';
import { METRICS_CONFIG } from '../config';
import { palette } from '../shared/theme';
import { CurrentMonthLabel } from '../shared/CurrentMonthLabel';

interface SessionMetricsProps {
  filters: BaseAnalyticsFilter;
}

type SessionMetricItem = {
  id: string;
  label: ReactNode;
  value: string;
  secondaryValue?: {
    value: string;
    label: ReactNode;
    color: string;
  };
  color: string;
  helpTooltip?: ReactNode;
};

function SessionMetricsContent({ filters }: SessionMetricsProps) {
  const isMobile = useIsMobile();
  const { data: sessionMinutes, isLoading: isLoadingMinutes } = useSessionMinutes(filters);
  const { data: sensorData, isLoading: isLoadingSensor } = useSessionSensorUtilization(filters);
  const hasEndDateFilter = Boolean(filters?.endDate);

  const kpiItems: SessionMetricItem[] = useMemo(() => {
    const totalSessionsAllTime =
      sessionMinutes?.totalSessionsAllTime ?? METRICS_CONFIG.DEFAULT_VALUE;
    const totalSessionsCurrentMonth =
      sessionMinutes?.totalSessionsCurrentMonth ?? METRICS_CONFIG.DEFAULT_VALUE;
    const totalMinutesAllTime = sessionMinutes?.totalMinutesAllTime ?? METRICS_CONFIG.DEFAULT_VALUE;
    const totalMinutesCurrentMonth =
      sessionMinutes?.totalMinutesCurrentMonth ?? METRICS_CONFIG.DEFAULT_VALUE;
    const sensorUtilizationRateAllTime =
      sensorData?.sensorUtilizationRateAllTime ?? METRICS_CONFIG.DEFAULT_VALUE;
    const sensorUtilizationRateCurrentMonth =
      sensorData?.sensorUtilizationRateCurrentMonth ?? METRICS_CONFIG.DEFAULT_VALUE;

    return [
      {
        id: 'sessions',
        label: 'Total Sessions',
        value: chartUtils.formatNumber(totalSessionsAllTime),
        secondaryValue: hasEndDateFilter
          ? undefined
          : {
              value: chartUtils.formatNumber(totalSessionsCurrentMonth),
              label: <CurrentMonthLabel />,
              color: palette.indigo,
            },
        color: palette.indigo,
      },
      {
        id: 'minutes',
        label: 'Total Minutes',
        value: formatNumber(totalMinutesAllTime),
        secondaryValue: hasEndDateFilter
          ? undefined
          : {
              value: formatNumber(totalMinutesCurrentMonth),
              label: <CurrentMonthLabel />,
              color: palette.cyan,
            },
        color: palette.cyan,
      },
      {
        id: 'sensor-utilization',
        label: 'Biosensor Utilization',
        value: `${sensorUtilizationRateAllTime}%`,
        secondaryValue: hasEndDateFilter
          ? undefined
          : {
              value: `${chartUtils.formatNumber(sensorUtilizationRateCurrentMonth)}%`,
              label: <CurrentMonthLabel />,
              color: palette.indigo,
            },
        color: palette.red,
        helpTooltip: (
          <MetricHelpTooltip
            title="What this measures"
            description="Percentage of sessions that captured biosensor data."
          />
        ),
      },
    ];
  }, [
    sessionMinutes?.totalSessionsAllTime,
    sessionMinutes?.totalSessionsCurrentMonth,
    sessionMinutes?.totalMinutesAllTime,
    sessionMinutes?.totalMinutesCurrentMonth,
    sensorData?.sensorUtilizationRateAllTime,
    sensorData?.sensorUtilizationRateCurrentMonth,
    hasEndDateFilter,
  ]);

  if (isLoadingMinutes || isLoadingSensor) {
    return <SkeletonLoader variant="metrics" count={3} />;
  }

  return (
    <div className="space-y-2">
      <div
        className={`grid gap-3 ${isMobile ? 'grid-cols-2' : 'grid-cols-3'}`}
        style={{ ['--anl-metric-bg' as any]: 'var(--anl-surface)' }}
      >
        {kpiItems.map(item => (
          <MetricCard
            key={item.id}
            value={item.value}
            label={item.label}
            secondaryValue={item.secondaryValue?.value}
            secondaryLabel={item.secondaryValue?.label}
            accent={String(item.color)}
            preserveSecondarySpacing
            helpTooltip={item.helpTooltip}
          />
        ))}
      </div>
    </div>
  );
}

export function SessionMetrics({ filters }: SessionMetricsProps) {
  return (
    <AnalyticsErrorBoundary>
      <SessionMetricsContent filters={filters} />
    </AnalyticsErrorBoundary>
  );
}
